import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";
import { useApi } from "../lib/api";
import { formatDate, slaRemaining } from "../lib/format";
import { PriorityBadge, StatusBadge } from "../components/shared/Badge";
import { Avatar } from "../components/shared/Avatar";
import { ContactPicker } from "../components/shared/ContactPicker";
import { hasPermission } from "../lib/permissions";
import { saveTicketsUrl } from "../lib/routeMemory";
import { useT } from "../i18n/I18nProvider";
import type {
  Agent,
  Contact,
  PaginatedResponse,
  SlaPolicy,
  Team,
  TicketPriority,
  TicketStatus,
  TicketSummary,
} from "@shared/types";

const PAGE_SIZE = 25;
const KANBAN_PAGE_SIZE = 200;

// What "no status filter" means on the list/team views: every open
// status, but neither closed nor canceled. Operators almost always
// want to triage in-flight work first; only when they go looking
// for an archived ticket do they actively re-enable those buckets.
// The kanban view never inherits this default — it shows every
// column so the board stays usable.
const DEFAULT_OPEN_STATUSES: TicketStatus[] = ["new", "working", "waiting_feedback", "resolved"];

type SortField =
  | "ticket_number"
  | "subject"
  | "status"
  | "priority"
  | "sla_due_at"
  | "last_message_at"
  | "created_at";
type SortDir = "asc" | "desc";

interface NewTicketForm {
  subject: string;
  body: string;
  priority: TicketPriority;
  contactId: string;
  teamId: string;
  slaPolicyId: string;
  assigneeId: string;
  tags: string;
  isPrivate: boolean;
  inboundAlias: string;
}

const EMPTY_NEW_TICKET: NewTicketForm = {
  subject: "",
  body: "",
  priority: "medium",
  contactId: "",
  teamId: "",
  slaPolicyId: "",
  assigneeId: "",
  tags: "",
  isPrivate: false,
  inboundAlias: "",
};

interface InlineContactDraft { firstName: string; lastName: string; email: string; phone: string }
const EMPTY_INLINE_CONTACT: InlineContactDraft = { firstName: "", lastName: "", email: "", phone: "" };

interface InlineAgentDraft { name: string; email: string; teamId: string }
const EMPTY_INLINE_AGENT: InlineAgentDraft = { name: "", email: "", teamId: "" };

interface InlineTeamDraft { name: string }
const EMPTY_INLINE_TEAM: InlineTeamDraft = { name: "" };

// Statuses that can appear as kanban columns. Canceled is left out
// of the visible columns by default — it's still a valid Move-menu
// target and still filterable from the chip strip on the kanban
// view (toggling 'canceled' on adds the column).
const KANBAN_COLUMNS: TicketStatus[] = ["new", "working", "waiting_feedback", "resolved", "closed", "canceled"];

interface TicketStats {
  byStatus: Partial<Record<TicketStatus, number>>;
  byPriority: Partial<Record<TicketPriority, number>>;
  openTotal: number;
  assignedToMe: number;
  avgFirstResponseMins: number | null;
  resolutionRate: number;
  slaAdherence: number;
  slaBreached: number;
  total: number;
  // Denominator the backend used for the resolution rate
  // (total - canceled). The "X of Y resolved or closed" caption
  // reads from this so canceled tickets don't show up in the Y.
  resolutionDenom: number;
}

function formatMinutes(mins: number | null | undefined): string {
  if (mins == null || !Number.isFinite(mins)) return "—";
  if (mins < 1) return "<1m";
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins - h * 60);
  if (h < 24) return m === 0 ? `${h}u` : `${h}u ${m}m`;
  const d = Math.floor(h / 24);
  const remH = h - d * 24;
  return remH === 0 ? `${d}d` : `${d}d ${remH}u`;
}

export default function TicketList() {
  const api = useApi();
  const t = useT();
  const navigate = useNavigate();
  const { user } = useDotBotsAuth();
  const canCreate = hasPermission(user?.roles ?? [], "tickets.write");
  const canMove = hasPermission(user?.roles ?? [], "tickets.write");
  // Inline create flows on the new-ticket form: each one is gated on
  // the same permission the dedicated admin/contacts page would
  // require, so a viewer or read-only agent doesn't see the "+" buttons.
  const canCreateContact = hasPermission(user?.roles ?? [], "contacts.write");
  const canCreateAgent = hasPermission(user?.roles ?? [], "agents.write");
  const canCreateTeam = hasPermission(user?.roles ?? [], "agents.write");

  // All filter/view state lives in the URL so a refresh restores it. The
  // route-memory layer in main.tsx writes the full URL (including search) to
  // localStorage and replays it on the next mount.
  const [searchParams, setSearchParams] = useSearchParams();

  // Pass the current list filters along when navigating to a ticket
  // detail so the detail page's prev/next walks the same filtered +
  // sorted set the operator was looking at (see /neighbors backend).
  const listSearch = searchParams.toString();
  const detailTo = (id: string) => ({ pathname: `/tickets/${id}`, search: listSearch });

  // Remember the last filtered ticket-list URL so the sidebar and
  // bottom-nav "Tickets" link returns the operator to the same view
  // when they bounce off to /contacts and back. Strip the page param
  // so the saved URL doesn't pin them to page 7 of yesterday's view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    next.delete("new");
    const qs = next.toString();
    saveTicketsUrl(qs ? `/tickets?${qs}` : "/tickets");
  }, [searchParams]);

  const view: "list" | "kanban" | "team" =
    searchParams.get("view") === "kanban" ? "kanban"
    : searchParams.get("view") === "team" ? "team"
    : "list";
  const search = searchParams.get("q") ?? "";
  const statusKey = searchParams.get("status") ?? "";
  const priorityKey = searchParams.get("priority") ?? "";
  // Comma-separated SLA states (breached / risk / ok / none) — same buckets
  // the slaRemaining helper produces locally.
  const slaKey = searchParams.get("sla") ?? "";
  // "awaiting=1" narrows the list to tickets where the customer is
  // waiting on us — mirrors the isAwaitingReply badge on the row.
  const awaitingOn = searchParams.get("awaiting") === "1";
  // Top-level scope tab: "" = all, "mine" = assigned to me, or a team UUID.
  const scope = searchParams.get("scope") ?? "";
  // Optional contact filter — when set, the list narrows to that
  // contact's tickets and a chip appears so the operator can see
  // (and clear) the filter. Set via deep link from the contacts
  // page (#/tickets?contactId=…).
  const contactId = searchParams.get("contactId") ?? "";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const sortField = (searchParams.get("sf") as SortField | null) ?? null;
  const sortDir: SortDir = searchParams.get("sd") === "asc" ? "asc" : "desc";
  const sort = useMemo(() => ({ field: sortField, dir: sortDir }), [sortField, sortDir]);

  // Apply a partial param patch without losing the rest of the query string.
  // Pass undefined to drop a key.
  const patchParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v == null || v === "") next.delete(k);
          else next.set(k, v);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const setView = (v: "list" | "kanban" | "team") =>
    patchParams({ view: v === "list" ? undefined : v });
  const setSearch = (v: string) => patchParams({ q: v || undefined, page: undefined });
  const setStatusKey = (v: string) => patchParams({ status: v || undefined, page: undefined });
  const setPriorityKey = (v: string) => patchParams({ priority: v || undefined, page: undefined });
  const setSlaKey = (v: string) => patchParams({ sla: v || undefined, page: undefined });
  const setScope = (v: string) => patchParams({ scope: v || undefined, page: undefined });
  const setContactId = (v: string) => patchParams({ contactId: v || undefined, page: undefined });
  const clearContactFilter = () => patchParams({ contactId: undefined, page: undefined });
  const toggleAwaiting = () => patchParams({ awaiting: awaitingOn ? undefined : "1", page: undefined });
  const setPage = (n: number) => patchParams({ page: n > 1 ? String(n) : undefined });
  const setSort = (next: { field: SortField | null; dir: SortDir }) => patchParams({
    sf: next.field ?? undefined,
    sd: next.field ? next.dir : undefined,
    page: undefined,
  });

  const [refreshTick, setRefreshTick] = useState(0);

  const [result, setResult] = useState<PaginatedResponse<TicketSummary> | null>(null);
  const [stats, setStats] = useState<TicketStats | null>(null);
  // Name of the contact the list is filtered to (when contactId is
  // in the URL). Fetched separately so the chip can show a friendly
  // label even when the result set is empty.
  const [contactFilterName, setContactFilterName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<NewTicketForm>(EMPTY_NEW_TICKET);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [slaPolicies, setSlaPolicies] = useState<SlaPolicy[]>([]);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Aliases the current agent is allowed to pick as the outbound From
  // address on the new ticket. Refetched whenever the team changes so
  // the dropdown reflects the team's own aliases too.
  const [aliasPool, setAliasPool] = useState<{ agentAliases: string[]; teamAliases: string[] }>({
    agentAliases: [],
    teamAliases: [],
  });

  // Inline-create state for contact / agent / team while drafting a
  // ticket. null means closed; an object means the mini-form is open.
  const [inlineContact, setInlineContact] = useState<InlineContactDraft | null>(null);
  const [inlineAgent, setInlineAgent] = useState<InlineAgentDraft | null>(null);
  const [inlineTeam, setInlineTeam] = useState<InlineTeamDraft | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [inlineSaving, setInlineSaving] = useState(false);

  // Effective status filter, with the DEFAULT_OPEN_STATUSES applied
  // when the operator hasn't actively picked anything. Used for the
  // chip UI (so the four default chips look selected), the API
  // params below (so the backend gets the same shortlist) and the
  // kanban column visibility (so closed/canceled columns stay
  // hidden until the operator toggles them in). isTabularView is
  // kept around for the few remaining tabular-only knobs (sort,
  // pagination) — status itself now travels on every view.
  const isTabularView = view === "list" || view === "team";
  const effectiveStatusKey = statusKey || DEFAULT_OPEN_STATUSES.join(",");

  // Derive Sets from the comma-separated URL params so the chip filter still
  // gets its expected shape.
  const statusSel = useMemo(
    () => new Set(effectiveStatusKey.split(",").filter(Boolean) as TicketStatus[]),
    [effectiveStatusKey],
  );
  const prioritySel = useMemo(
    () => new Set(priorityKey.split(",").filter(Boolean) as TicketPriority[]),
    [priorityKey],
  );
  type SlaState = "breached" | "risk" | "ok" | "none";
  const slaSel = useMemo(
    () => new Set(slaKey.split(",").filter(Boolean) as SlaState[]),
    [slaKey],
  );

  function toggleSet<T extends string>(set: Set<T>, value: T): string {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    return [...next].sort().join(",");
  }

  // Teams power the team-tab strip at the top of the list. Loaded once at
  // mount so switching tabs is instant; refreshes follow the same lifecycle
  // as agents/contacts loaded by startCreate.
  useEffect(() => {
    api.get<{ data: Team[] }>("/api/teams").then((r) => setTeams(r.data)).catch(() => {});
  }, [api]);

  // Contacts power the per-contact filter in the filters card.
  // Loaded once at mount alongside teams so the dropdown is ready
  // when the operator opens the filter row. The API caps at 200 —
  // good enough for the typical operator; a "type to search" build
  // can replace this if the list grows.
  useEffect(() => {
    api.get<{ data: Contact[] }>("/api/contacts")
      .then((r) => setContacts(r.data))
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    // Status filter now travels with every view, including kanban —
    // operators want to toggle closed/canceled columns from the same
    // chip strip on every layout. The kanban grid hides columns
    // whose status isn't in the active filter (see KanbanView).
    if (effectiveStatusKey) params.set("status", effectiveStatusKey);
    if (priorityKey) params.set("priority", priorityKey);
    if (slaKey) params.set("sla", slaKey);
    if (awaitingOn) params.set("awaitingReply", "1");
    if (scope === "mine") params.set("assignedToMe", "1");
    else if (scope === "noteam") params.set("noTeam", "1");
    else if (scope) params.set("teamId", scope);
    if (contactId) params.set("contactId", contactId);
    if (sort.field && isTabularView) params.set("sort", `${sort.field}:${sort.dir}`);
    params.set("page", String(isTabularView ? page : 1));
    params.set("pageSize", String(isTabularView ? PAGE_SIZE : KANBAN_PAGE_SIZE));

    setLoading(true);
    const id = setTimeout(async () => {
      try {
        // Run stats + list in parallel; the list query is the slow one and
        // we don't want stats to block its render.
        const [res] = await Promise.all([
          api.get<PaginatedResponse<TicketSummary>>(`/api/tickets?${params.toString()}`),
          api.get<TicketStats>("/api/tickets/stats").then(setStats).catch(() => {}),
        ]);
        setResult(res);
      } finally {
        setLoading(false);
      }
    }, view === "kanban" ? 0 : 200);
    return () => clearTimeout(id);
  }, [api, search, effectiveStatusKey, isTabularView, priorityKey, slaKey, awaitingOn, scope, contactId, page, sort, view, refreshTick]);

  // Resolve the contact's name for the filter chip. Lazy — only
  // fires when contactId actually changes, and falls back to the
  // raw id when the fetch fails (e.g. the contact was deleted).
  useEffect(() => {
    if (!contactId) {
      setContactFilterName(null);
      return;
    }
    let cancelled = false;
    api.get<{ contact: { first_name: string | null; last_name: string | null; email: string | null } }>(
      `/api/contacts/${encodeURIComponent(contactId)}`,
    )
      .then((res) => {
        if (cancelled) return;
        const name = [res.contact.first_name, res.contact.last_name].filter(Boolean).join(" ").trim();
        setContactFilterName(name || res.contact.email || contactId);
      })
      .catch(() => { if (!cancelled) setContactFilterName(contactId); });
    return () => { cancelled = true; };
  }, [api, contactId]);

  const refresh = () => setRefreshTick((n) => n + 1);

  // Auto-refresh the list every 30s so newly-arrived inbound mail
  // surfaces without operators having to hit refresh. Paused when the
  // tab is hidden, and we fire an immediate refresh when it regains
  // focus so anything that arrived during the pause shows up right
  // away. Re-uses the existing refreshTick channel so it benefits
  // from the same debouncing and parallel stats fetch the manual
  // refresh button does.
  useEffect(() => {
    const POLL_INTERVAL_MS = 30_000;
    let timer: number | null = null;
    const start = () => {
      if (timer != null) return;
      timer = window.setInterval(refresh, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timer == null) return;
      window.clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const toggleSort = (field: SortField) => {
    let next: { field: SortField | null; dir: SortDir };
    if (sort.field !== field) next = { field, dir: "asc" };
    else if (sort.dir === "asc") next = { field, dir: "desc" };
    else next = { field: null, dir: "desc" }; // third click clears the sort
    setSort(next);
  };

  const startCreate = useCallback(async () => {
    setCreateError(null);
    setDraft(EMPTY_NEW_TICKET);
    setAliasPool({ agentAliases: [], teamAliases: [] });
    setCreating(true);
    try {
      const [c, a, tm, s, aliases] = await Promise.all([
        api.get<{ data: Contact[] }>("/api/contacts"),
        api.get<{ data: Agent[] }>("/api/agents"),
        api.get<{ data: Team[] }>("/api/teams"),
        api.get<{ data: SlaPolicy[] }>("/api/sla-policies"),
        api.get<{ agentAliases: string[]; teamAliases: string[] }>("/api/me/aliases"),
      ]);
      setContacts(c.data);
      setAgents(a.data);
      setTeams(tm.data);
      setSlaPolicies(s.data);
      setAliasPool(aliases);
    } catch (err) {
      setCreateError((err as Error).message);
    }
  }, [api]);

  // Refetch the alias pool when the picked team changes so team_emails
  // for the newly-selected team appear in the dropdown. Skipped while
  // the create form is closed (nothing to render into).
  useEffect(() => {
    if (!creating) return;
    let cancelled = false;
    const teamQs = draft.teamId ? `?teamId=${encodeURIComponent(draft.teamId)}` : "";
    api
      .get<{ agentAliases: string[]; teamAliases: string[] }>(`/api/me/aliases${teamQs}`)
      .then((res) => {
        if (!cancelled) setAliasPool(res);
      })
      .catch(() => {
        // Non-fatal — the dropdown just stays empty and the user can
        // submit without a chosen alias, which falls back to the
        // default resolveReplyFromAddress cascade server-side.
      });
    return () => { cancelled = true; };
  }, [api, creating, draft.teamId]);

  // Auto-default the alias when the user hasn't picked one manually:
  // private → first agent-alias, otherwise → first team-alias (with
  // agent-alias as a fallback when no team-alias exists). Runs only
  // when the current value isn't already in the (possibly refreshed)
  // pool — otherwise we'd stomp on the user's explicit choice.
  useEffect(() => {
    if (!creating) return;
    const combined = [...aliasPool.agentAliases, ...aliasPool.teamAliases];
    if (draft.inboundAlias && combined.includes(draft.inboundAlias)) return;
    const next = draft.isPrivate
      ? (aliasPool.agentAliases[0] ?? aliasPool.teamAliases[0] ?? "")
      : (aliasPool.teamAliases[0] ?? aliasPool.agentAliases[0] ?? "");
    if (next !== draft.inboundAlias) {
      setDraft((d) => ({ ...d, inboundAlias: next }));
    }
  }, [creating, aliasPool, draft.isPrivate, draft.inboundAlias]);

  // Auto-open the create form when navigated here with ?new=1 (sidebar CTA).
  // We pop the param so a refresh after closing doesn't re-open it; clicking
  // the sidebar CTA again re-adds the param and the effect re-fires.
  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    startCreate();
    patchParams({ new: undefined });
  }, [searchParams, startCreate, patchParams]);

  const cancelCreate = () => {
    setCreating(false);
    setCreateError(null);
    setInlineContact(null);
    setInlineAgent(null);
    setInlineTeam(null);
    setInlineError(null);
  };

  const submitInlineContact = async () => {
    if (!inlineContact) return;
    const firstName = inlineContact.firstName.trim();
    const lastName = inlineContact.lastName.trim();
    // At least one of first / last must be filled — bedrijfscontacten
    // met alleen een achternaam of gedeelde inboxen met alleen een
    // voornaam-alias moeten allebei kunnen.
    if (!firstName && !lastName) {
      setInlineError(t("tickets.inlineRequired"));
      return;
    }
    setInlineSaving(true);
    setInlineError(null);
    try {
      const res = await api.post<{ id: string }>("/api/contacts", {
        firstName: firstName || null,
        lastName: lastName || null,
        email: inlineContact.email.trim() || null,
        phone: inlineContact.phone.trim() || null,
      });
      const list = await api.get<{ data: Contact[] }>("/api/contacts");
      setContacts(list.data);
      setDraft((d) => ({ ...d, contactId: res.id }));
      setInlineContact(null);
    } catch (err) {
      setInlineError((err as Error).message);
    } finally {
      setInlineSaving(false);
    }
  };

  const submitInlineAgent = async () => {
    if (!inlineAgent) return;
    if (!inlineAgent.name.trim()) {
      setInlineError(t("tickets.inlineRequired"));
      return;
    }
    setInlineSaving(true);
    setInlineError(null);
    try {
      const res = await api.post<{ id: string }>("/api/agents", {
        name: inlineAgent.name.trim(),
        email: inlineAgent.email.trim() || null,
        teamId: inlineAgent.teamId || null,
      });
      const list = await api.get<{ data: Agent[] }>("/api/agents");
      setAgents(list.data);
      setDraft((d) => ({ ...d, assigneeId: res.id }));
      setInlineAgent(null);
    } catch (err) {
      setInlineError((err as Error).message);
    } finally {
      setInlineSaving(false);
    }
  };

  const submitInlineTeam = async () => {
    if (!inlineTeam) return;
    if (!inlineTeam.name.trim()) {
      setInlineError(t("tickets.inlineRequired"));
      return;
    }
    setInlineSaving(true);
    setInlineError(null);
    try {
      const res = await api.post<{ id: string }>("/api/teams", { name: inlineTeam.name.trim() });
      const list = await api.get<{ data: Team[] }>("/api/teams");
      setTeams(list.data);
      setDraft((d) => ({ ...d, teamId: res.id }));
      setInlineTeam(null);
    } catch (err) {
      setInlineError((err as Error).message);
    } finally {
      setInlineSaving(false);
    }
  };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.contactId) {
      setCreateError(t("tickets.contactRequired"));
      return;
    }
    setSaving(true);
    setCreateError(null);
    try {
      const res = await api.post<{ id: string; ticketNumber: number }>("/api/tickets", {
        subject: draft.subject.trim(),
        body: draft.body.trim() || undefined,
        priority: draft.priority,
        contactId: draft.contactId,
        teamId: draft.teamId || undefined,
        slaPolicyId: draft.slaPolicyId || undefined,
        assigneeId: draft.assigneeId || undefined,
        tags: draft.tags.split(",").map((s) => s.trim()).filter(Boolean),
        isPrivate: draft.isPrivate,
        inboundAlias: draft.inboundAlias || undefined,
      });
      navigate(`/tickets/${res.id}`);
    } catch (err) {
      setCreateError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const moveTicket = async (ticketId: string, newStatus: TicketStatus) => {
    setMoveError(null);
    try {
      await api.patch(`/api/tickets/${ticketId}`, { status: newStatus });
      refresh();
    } catch (err) {
      setMoveError((err as Error).message);
    }
  };

  const totalPages = result ? Math.max(1, Math.ceil(result.total / PAGE_SIZE)) : 1;

  return (
    <>
      <div className="toolbar">
        <h2>{t("ticketList.title")}</h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div className="view-toggle">
            <button
              type="button"
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
            >
              {t("ticketList.viewList")}
            </button>
            <button
              type="button"
              className={view === "kanban" ? "active" : ""}
              onClick={() => setView("kanban")}
            >
              {t("ticketList.viewKanban")}
            </button>
            <button
              type="button"
              className={view === "team" ? "active" : ""}
              onClick={() => setView("team")}
            >
              {t("ticketList.viewTeam")}
            </button>
          </div>
          <button onClick={refresh} title={t("common.refresh")}>
            ⟳ {t("common.refresh")}
          </button>
          {canCreate && !creating && (
            <button className="primary" onClick={startCreate}>{t("tickets.newButton")}</button>
          )}
        </div>
      </div>

      {stats && <StatsRow stats={stats} />}

      {creating && (
        <form className="card" onSubmit={submitCreate}>
          <h3 style={{ marginTop: 0 }}>{t("tickets.newHeading")}</h3>

          <div className="field">
            <label>{t("ticket.subject")} <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({t("common.required")})</span></label>
            <input required value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
          </div>

          <div className="field">
            <label>{t("tickets.bodyField")}</label>
            <textarea
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder={t("tickets.bodyPlaceholder")}
            />
          </div>

          <div className="form-grid form-grid-2">
            <div className="field">
              <label>{t("tickets.contactField")} <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({t("common.required")})</span></label>
              <div className="inline-create-row">
                <ContactPicker
                  contacts={contacts}
                  selectedId={draft.contactId}
                  onChange={(id) => setDraft({ ...draft, contactId: id })}
                  placeholder={t("tickets.contactPlaceholder")}
                />
                {canCreateContact && !inlineContact && (
                  <button type="button" onClick={() => { setInlineContact(EMPTY_INLINE_CONTACT); setInlineError(null); }}>
                    + {t("contacts.newButton")}
                  </button>
                )}
              </div>
              {inlineContact && (
                <div className="inline-create-form">
                  <div className="form-grid form-grid-2">
                    <input placeholder={t("common.firstName")} value={inlineContact.firstName} onChange={(e) => setInlineContact({ ...inlineContact, firstName: e.target.value })} />
                    <input placeholder={t("common.lastName")} value={inlineContact.lastName} onChange={(e) => setInlineContact({ ...inlineContact, lastName: e.target.value })} />
                  </div>
                  <input placeholder={t("common.email")} type="email" value={inlineContact.email} onChange={(e) => setInlineContact({ ...inlineContact, email: e.target.value })} />
                  <input placeholder={t("common.phone")} value={inlineContact.phone} onChange={(e) => setInlineContact({ ...inlineContact, phone: e.target.value })} />
                  <div className="inline-create-actions">
                    <button type="button" onClick={() => { setInlineContact(null); setInlineError(null); }} disabled={inlineSaving}>{t("common.cancel")}</button>
                    <button type="button" className="primary" onClick={submitInlineContact} disabled={inlineSaving}>
                      {inlineSaving ? t("common.creating") : t("common.create")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="field">
              <label>{t("ticket.priority")}</label>
              <select value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value as TicketPriority })}>
                <option value="critical">{t("priority.critical")}</option>
                <option value="high">{t("priority.high")}</option>
                <option value="medium">{t("priority.medium")}</option>
                <option value="low">{t("priority.low")}</option>
              </select>
            </div>
          </div>

          <div className="form-grid form-grid-3">
            <div className="field">
              <label>{t("ticket.assignee")}</label>
              <div className="inline-create-row">
                <select value={draft.assigneeId} onChange={(e) => setDraft({ ...draft, assigneeId: e.target.value })}>
                  <option value="">{t("tickets.unassigned")}</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {canCreateAgent && !inlineAgent && (
                  <button type="button" onClick={() => { setInlineAgent(EMPTY_INLINE_AGENT); setInlineError(null); }}>
                    + {t("admin.agents.newButton")}
                  </button>
                )}
              </div>
              {inlineAgent && (
                <div className="inline-create-form">
                  <input placeholder={t("contacts.tableName")} value={inlineAgent.name} onChange={(e) => setInlineAgent({ ...inlineAgent, name: e.target.value })} />
                  <input placeholder={t("common.email")} type="email" value={inlineAgent.email} onChange={(e) => setInlineAgent({ ...inlineAgent, email: e.target.value })} />
                  <select value={inlineAgent.teamId} onChange={(e) => setInlineAgent({ ...inlineAgent, teamId: e.target.value })}>
                    <option value="">{t("tickets.noTeam")}</option>
                    {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                  </select>
                  <div className="inline-create-actions">
                    <button type="button" onClick={() => { setInlineAgent(null); setInlineError(null); }} disabled={inlineSaving}>{t("common.cancel")}</button>
                    <button type="button" className="primary" onClick={submitInlineAgent} disabled={inlineSaving}>
                      {inlineSaving ? t("common.creating") : t("common.create")}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="field">
              <label>{t("ticket.team")}</label>
              <div className="inline-create-row">
                <select value={draft.teamId} onChange={(e) => setDraft({ ...draft, teamId: e.target.value })}>
                  <option value="">{t("tickets.noTeam")}</option>
                  {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                </select>
                {canCreateTeam && !inlineTeam && (
                  <button type="button" onClick={() => { setInlineTeam(EMPTY_INLINE_TEAM); setInlineError(null); }}>
                    + {t("admin.teams.newButton")}
                  </button>
                )}
              </div>
              {inlineTeam && (
                <div className="inline-create-form">
                  <input placeholder={t("admin.teams.name")} value={inlineTeam.name} onChange={(e) => setInlineTeam({ name: e.target.value })} />
                  <div className="inline-create-actions">
                    <button type="button" onClick={() => { setInlineTeam(null); setInlineError(null); }} disabled={inlineSaving}>{t("common.cancel")}</button>
                    <button type="button" className="primary" onClick={submitInlineTeam} disabled={inlineSaving}>
                      {inlineSaving ? t("common.creating") : t("common.create")}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="field">
              <label>{t("ticket.sla")}</label>
              <select value={draft.slaPolicyId} onChange={(e) => setDraft({ ...draft, slaPolicyId: e.target.value })}>
                <option value="">{t("tickets.noSla")}</option>
                {slaPolicies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <label>{t("tickets.tagsField")}</label>
            <input value={draft.tags} onChange={(e) => setDraft({ ...draft, tags: e.target.value })} />
          </div>

          <div className="field">
            <label className="inline-checkbox">
              <input
                type="checkbox"
                checked={draft.isPrivate}
                onChange={(e) => setDraft({ ...draft, isPrivate: e.target.checked })}
              />
              {t("tickets.privateField")}
            </label>
            <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>{t("tickets.privateHelp")}</small>
          </div>

          <div className="field">
            <label>{t("tickets.inboundAliasField")}</label>
            <select
              value={draft.inboundAlias}
              onChange={(e) => setDraft({ ...draft, inboundAlias: e.target.value })}
            >
              <option value="">{t("tickets.inboundAliasDefault")}</option>
              {aliasPool.agentAliases.length > 0 && (
                <optgroup label={t("tickets.inboundAliasPersonalGroup")}>
                  {aliasPool.agentAliases.map((email) => (
                    <option key={`me-${email}`} value={email}>{email}</option>
                  ))}
                </optgroup>
              )}
              {aliasPool.teamAliases.length > 0 && (
                <optgroup label={t("tickets.inboundAliasTeamGroup")}>
                  {aliasPool.teamAliases
                    .filter((email) => !aliasPool.agentAliases.includes(email))
                    .map((email) => (
                      <option key={`team-${email}`} value={email}>{email}</option>
                    ))}
                </optgroup>
              )}
            </select>
            <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>{t("tickets.inboundAliasHelp")}</small>
          </div>

          {inlineError && <div style={{ color: "var(--danger)", marginBottom: 12 }}>{inlineError}</div>}
          {createError && <div style={{ color: "var(--danger)", marginBottom: 12 }}>{createError}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" onClick={cancelCreate}>{t("common.cancel")}</button>
            <button type="submit" className="primary" disabled={saving || !draft.subject.trim() || !draft.contactId}>
              {saving ? t("common.creating") : t("common.create")}
            </button>
          </div>
        </form>
      )}

      {/* Scope tabs: Mine / All / one per team. Active tab shows a blue
          underline. Horizontally scrollable on mobile when the team list
          is wide. */}
      <div className="ticket-scope-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={scope === "mine"}
          className={scope === "mine" ? "active" : ""}
          onClick={() => setScope("mine")}
        >
          {t("tickets.tabMine")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={scope === ""}
          className={scope === "" ? "active" : ""}
          onClick={() => setScope("")}
        >
          {t("tickets.tabAll")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={scope === "noteam"}
          className={scope === "noteam" ? "active" : ""}
          onClick={() => setScope("noteam")}
        >
          {t("tickets.tabNoTeam")}
        </button>
        {teams.map((tm) => (
          <button
            key={tm.id}
            type="button"
            role="tab"
            aria-selected={scope === tm.id}
            className={scope === tm.id ? "active" : ""}
            onClick={() => setScope(tm.id)}
          >
            {tm.name}
          </button>
        ))}
        {/* Orthogonal toggle for "customer is waiting on us". Sits at
            the end of the scope-tab strip so operators can flip it on
            top of any Mine/All/Team scope without losing that context.
            Styled with badge-awaiting so an active state reads the
            same as the row badge. */}
        <button
          type="button"
          role="tab"
          aria-selected={awaitingOn}
          className={`ticket-scope-awaiting ${awaitingOn ? "active" : ""}`}
          onClick={toggleAwaiting}
          title={t("ticketList.awaitingReply")}
        >
          {t("ticketList.awaitingReply")}
        </button>
      </div>

      {(contactId || awaitingOn) && (
        <div className="card filter-chip-bar">
          {awaitingOn && (
            <span className="filter-chip">
              <strong>{t("ticketList.awaitingReply")}</strong>
              <button
                type="button"
                className="filter-chip-x"
                aria-label={t("ticketList.awaitingReply")}
                title={t("ticketList.awaitingReply")}
                onClick={toggleAwaiting}
              >
                ✕
              </button>
            </span>
          )}
          {contactId && (
            <span className="filter-chip">
              <span className="filter-chip-label">{t("ticketList.contactFilter")}</span>
              <strong>{contactFilterName ?? t("common.loading")}</strong>
              <button
                type="button"
                className="filter-chip-x"
                aria-label={t("ticketList.clearContactFilter")}
                title={t("ticketList.clearContactFilter")}
                onClick={clearContactFilter}
              >
                ✕
              </button>
            </span>
          )}
        </div>
      )}

      <div className="filters card">
        <div className="field" style={{ flex: "1 1 240px", minWidth: 200 }}>
          <label>{t("common.search")}</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("ticket.searchPlaceholder")} />
        </div>
        <div className="field" style={{ flex: "2 1 320px" }}>
          <label>{t("filter.status")}</label>
          <ChipFilter<TicketStatus>
            options={["new", "working", "waiting_feedback", "resolved", "closed", "canceled"]}
            selected={statusSel}
            onToggle={(s) => setStatusKey(toggleSet(statusSel, s))}
            renderLabel={(s) => {
              const c = stats?.byStatus?.[s];
              return c != null ? `${t(`status.${s}`)} (${c})` : t(`status.${s}`);
            }}
            chipClass={(s) => `status-${s}`}
          />
        </div>
        <div className="field" style={{ flex: "1 1 280px" }}>
          <label>{t("filter.priority")}</label>
          <ChipFilter<TicketPriority>
            options={["critical", "high", "medium", "low"]}
            selected={prioritySel}
            onToggle={(p) => setPriorityKey(toggleSet(prioritySel, p))}
            renderLabel={(p) => {
              const c = stats?.byPriority?.[p];
              return c != null ? `${t(`priority.${p}`)} (${c})` : t(`priority.${p}`);
            }}
            chipClass={(p) => `priority-${p}`}
          />
        </div>
        <div className="field" style={{ flex: "1 1 240px" }}>
          <label>{t("filter.sla")}</label>
          <ChipFilter<SlaState>
            options={["breached", "risk"]}
            selected={slaSel}
            onToggle={(s) => setSlaKey(toggleSet(slaSel, s))}
            renderLabel={(s) => t(`filter.sla.${s}`)}
            chipClass={(s) => `sla-chip-${s}`}
          />
        </div>
        <div className="field" style={{ flex: "1 1 220px", minWidth: 180 }}>
          <label>{t("filter.contact")}</label>
          <select value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">{t("filter.allContacts")}</option>
            {contacts.map((c) => {
              const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
              const display = name || c.email || c.id;
              return <option key={c.id} value={c.id}>{display}</option>;
            })}
          </select>
        </div>
      </div>

      {moveError && <div className="card" style={{ color: "var(--danger)" }}>{moveError}</div>}

      {view === "list" && (
        <ListView
          rows={result?.data ?? []}
          loading={loading}
          sort={sort}
          onSort={toggleSort}
          page={page}
          totalPages={totalPages}
          onPage={setPage}
          detailTo={detailTo}
        />
      )}
      {view === "kanban" && (
        <KanbanView
          rows={result?.data ?? []}
          loading={loading}
          canMove={canMove}
          onMove={moveTicket}
          visibleStatuses={statusSel}
          detailTo={detailTo}
        />
      )}
      {view === "team" && (
        <TeamView
          rows={result?.data ?? []}
          loading={loading}
          sort={sort}
          onSort={toggleSort}
          page={page}
          totalPages={totalPages}
          onPage={setPage}
          teams={teams}
          canBulkAssign={canMove}
          onBulkAssigned={refresh}
          detailTo={detailTo}
        />
      )}
    </>
  );
}

function StatsRow({ stats }: { stats: TicketStats }) {
  const t = useT();
  const slaPct = Math.round(stats.slaAdherence * 100);
  const resPct = Math.round(stats.resolutionRate * 100);
  return (
    <div className="ticket-stats-row">
      <div className="card ticket-stat">
        <div className="ticket-stat-label">{t("stats.slaAdherence")}</div>
        <div className="ticket-stat-value">{slaPct}%</div>
        <div className="ticket-stat-bar"><div className="ticket-stat-bar-fill" style={{ width: `${slaPct}%`, background: slaPct >= 95 ? "var(--ok)" : slaPct >= 80 ? "var(--warn)" : "var(--danger)" }} /></div>
        <div className="ticket-stat-foot">{t("stats.slaInBreach", { n: stats.slaBreached })}</div>
      </div>
      <div className="card ticket-stat">
        <div className="ticket-stat-label">{t("stats.firstResponse")}</div>
        <div className="ticket-stat-value">{formatMinutes(stats.avgFirstResponseMins)}</div>
        <div className="ticket-stat-foot">{t("stats.firstResponseHint")}</div>
      </div>
      <div className="card ticket-stat">
        <div className="ticket-stat-label">{t("stats.resolutionRate")}</div>
        <div className="ticket-stat-value">{resPct}%</div>
        <div className="ticket-stat-foot">{t("stats.resolvedOf", { resolved: Math.round(stats.resolutionDenom * stats.resolutionRate), total: stats.resolutionDenom })}</div>
      </div>
      <div className="card ticket-stat">
        <div className="ticket-stat-label">{t("stats.assignedToYou")}</div>
        <div className="ticket-stat-value">{stats.assignedToMe}</div>
        <div className="ticket-stat-foot">{t("stats.openTotal", { n: stats.openTotal })}</div>
      </div>
    </div>
  );
}

function ContactCell({ name, email }: { name: string | null; email: string | null }) {
  const label = (name && name.trim()) || email || "—";
  const sub = name && name.trim() && email ? email : null;
  return (
    <div className="contact-cell">
      <Avatar name={label} size={28} />
      <div className="contact-cell-text">
        <div className="contact-cell-name">{label}</div>
        {sub && <div className="contact-cell-sub">{sub}</div>}
      </div>
    </div>
  );
}

interface ChipFilterProps<T extends string> {
  options: T[];
  selected: Set<T>;
  onToggle: (value: T) => void;
  renderLabel: (value: T) => string;
  chipClass: (value: T) => string;
}

function ChipFilter<T extends string>({ options, selected, onToggle, renderLabel, chipClass }: ChipFilterProps<T>) {
  return (
    <div className="filter-chips">
      {options.map((opt) => {
        const active = selected.has(opt);
        return (
          <button
            type="button"
            key={opt}
            className={`chip badge ${chipClass(opt)} ${active ? "active" : "inactive"}`}
            onClick={() => onToggle(opt)}
            aria-pressed={active}
          >
            {renderLabel(opt)}
          </button>
        );
      })}
    </div>
  );
}

interface DetailTarget { pathname: string; search: string }
type DetailToFn = (id: string) => DetailTarget;

// True when the customer was the last to speak on a still-open ticket
// — i.e. the agent owes a reply. Closed/canceled tickets don't qualify
// (no action expected from the agent). A manual dismiss stamps
// awaiting_reply_cleared_at; the flag returns automatically the moment
// a fresh inbound message bumps last_message_at past that timestamp.
function isAwaitingReply(tk: TicketSummary): boolean {
  if (tk.last_message_direction !== "inbound") return false;
  if (tk.status === "closed" || tk.status === "canceled") return false;
  if (tk.awaiting_reply_cleared_at && tk.last_message_at
      && tk.awaiting_reply_cleared_at >= tk.last_message_at) {
    return false;
  }
  return true;
}

interface ListViewProps {
  rows: TicketSummary[];
  loading: boolean;
  sort: { field: SortField | null; dir: SortDir };
  onSort: (field: SortField) => void;
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
  detailTo: DetailToFn;
}

function ListView({ rows, loading, sort, onSort, page, totalPages, onPage, detailTo }: ListViewProps) {
  const t = useT();

  function header(field: SortField, label: string) {
    const active = sort.field === field;
    const arrow = active ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
    return (
      <th
        onClick={() => onSort(field)}
        style={{ cursor: "pointer", userSelect: "none" }}
        title={t("ticketList.sortHint")}
      >
        {label}{arrow}
      </th>
    );
  }

  return (
    <div className="card">
      {loading && <p>{t("common.loading")}</p>}

      {/* Desktop / tablet: table. Hidden by CSS below 640 px. */}
      <div className="tickets-table-wrap">
        <table>
          <thead>
            <tr>
              {header("ticket_number", t("ticket.number"))}
              {header("subject", t("ticket.subject"))}
              <th>{t("ticket.contact")}</th>
              {header("status", t("ticket.status"))}
              {header("priority", t("ticket.priority"))}
              {header("sla_due_at", t("ticket.sla"))}
              {header("last_message_at", t("ticket.lastUpdate"))}
            </tr>
          </thead>
          <tbody>
            {rows.map((tk) => {
              const sla = slaRemaining(tk.sla_due_at, tk.status);
              return (
                <tr key={tk.id}>
                  <td><Link to={detailTo(tk.id)}>#{tk.ticket_number}</Link></td>
                  <td>{tk.subject}{tk.is_private && <span className="badge role-badge" style={{ marginLeft: 6 }}>{t("tickets.privateBadge")}</span>}{isAwaitingReply(tk) && <span className="badge badge-awaiting" style={{ marginLeft: 6 }}>{t("ticketList.awaitingReply")}</span>}</td>
                  <td><ContactCell name={[tk.contact_first_name, tk.contact_last_name].filter(Boolean).join(" ") || null} email={tk.contact_email} /></td>
                  <td><StatusBadge status={tk.status} /></td>
                  <td><PriorityBadge priority={tk.priority} /></td>
                  <td className={`sla-${sla.state}`}>{sla.label}</td>
                  <td>{formatDate(tk.last_message_at ?? tk.updated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards. Visible only below 640 px. */}
      <div className="tickets-cards">
        {rows.map((tk) => {
          const sla = slaRemaining(tk.sla_due_at, tk.status);
          const contactLabel = [tk.contact_first_name, tk.contact_last_name].filter(Boolean).join(" ") || tk.contact_email || "—";
          return (
            <Link key={tk.id} to={detailTo(tk.id)} className="ticket-card-link">
              <article className="ticket-card">
                <header className="ticket-card-header">
                  <span className="ticket-card-num">#{tk.ticket_number}</span>
                  <StatusBadge status={tk.status} />
                </header>
                <div className="ticket-card-subject">
                  {tk.subject}
                  {tk.is_private && <span className="badge role-badge" style={{ marginLeft: 6 }}>{t("tickets.privateBadge")}</span>}{isAwaitingReply(tk) && <span className="badge badge-awaiting" style={{ marginLeft: 6 }}>{t("ticketList.awaitingReply")}</span>}
                </div>
                <div className="ticket-card-contact">{contactLabel}</div>
                <div className="ticket-card-meta">
                  <PriorityBadge priority={tk.priority} />
                  {tk.sla_due_at && <span className={`sla-${sla.state}`}>{t("ticket.sla")}: {sla.label}</span>}
                  <span style={{ marginLeft: "auto" }}>{formatDate(tk.last_message_at ?? tk.updated_at)}</span>
                </div>
              </article>
            </Link>
          );
        })}
        {!loading && rows.length === 0 && (
          <p style={{ color: "var(--text-muted)", textAlign: "center", padding: 12 }}>
            {t("ticketList.kanbanEmpty")}
          </p>
        )}
      </div>

      <div className="pagination">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)}>{t("common.previous")}</button>
        <span style={{ alignSelf: "center" }}>{t("common.page")} {page} {t("common.of")} {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => onPage(page + 1)}>{t("common.next")}</button>
      </div>
    </div>
  );
}

interface TeamViewProps {
  rows: TicketSummary[];
  loading: boolean;
  sort: { field: SortField | null; dir: SortDir };
  onSort: (field: SortField) => void;
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
  teams: Team[];
  canBulkAssign: boolean;
  onBulkAssigned: () => void;
  detailTo: DetailToFn;
}

// Same shape as ListView, but the SLA + last-update columns are
// replaced by a checkbox column and a Team column. The toolbar at the
// top lets the user bulk-assign the selected rows to a team.
function TeamView({
  rows, loading, sort, onSort, page, totalPages, onPage,
  teams, canBulkAssign, onBulkAssigned, detailTo,
}: TeamViewProps) {
  const t = useT();
  const api = useApi();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTeamId, setBulkTeamId] = useState<string>("");
  const [bulkStatus, setBulkStatus] = useState<string>("");
  const [applying, setApplying] = useState(false);
  const [applyingStatus, setApplyingStatus] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkInfo, setBulkInfo] = useState<string | null>(null);

  // Drop selections that no longer exist in the current page so the
  // counter doesn't lie after the page changes or a refresh trims rows.
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(rows.map((r) => r.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [rows]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelected = !allSelected && rows.some((r) => selected.has(r.id));

  const toggleAll = () => {
    setSelected((prev) => {
      if (rows.every((r) => prev.has(r.id))) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyBulk = async () => {
    if (selected.size === 0) return;
    setApplying(true);
    setBulkError(null);
    setBulkInfo(null);
    try {
      await api.post<{ updated: number }>("/api/tickets/bulk/team", {
        ticketIds: [...selected],
        teamId: bulkTeamId || null,
      });
      setSelected(new Set());
      onBulkAssigned();
    } catch (err) {
      setBulkError((err as Error).message);
    } finally {
      setApplying(false);
    }
  };

  const applyBulkStatus = async () => {
    if (selected.size === 0 || !bulkStatus) return;
    setApplyingStatus(true);
    setBulkError(null);
    setBulkInfo(null);
    try {
      const total = selected.size;
      const res = await api.post<{
        updated: number;
        skipped: { id: string; reason: string; from?: string }[];
      }>("/api/tickets/bulk/status", {
        ticketIds: [...selected],
        status: bulkStatus,
      });
      // Tell the operator how many actually moved; some may have been
      // already in the target state or had an illegal transition (e.g.
      // moving a 'closed' ticket to 'waiting_feedback'). Showing the
      // exact count avoids the silent feeling of "did it work?".
      setBulkInfo(
        res.skipped.length > 0
          ? t("ticketList.bulkStatusResult", {
              n: res.updated,
              total,
              skipped: res.skipped.length,
            })
          : t("ticketList.bulkStatusResultClean", { n: res.updated, total }),
      );
      setSelected(new Set());
      setBulkStatus("");
      onBulkAssigned();
    } catch (err) {
      setBulkError((err as Error).message);
    } finally {
      setApplyingStatus(false);
    }
  };

  function header(field: SortField, label: string) {
    const active = sort.field === field;
    const arrow = active ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
    return (
      <th
        onClick={() => onSort(field)}
        style={{ cursor: "pointer", userSelect: "none" }}
        title={t("ticketList.sortHint")}
      >
        {label}{arrow}
      </th>
    );
  }

  return (
    <div className="card">
      {canBulkAssign && (
        <div className="bulk-bar">
          <span className="bulk-count">
            {selected.size > 0
              ? t("ticketList.bulkSelected", { n: selected.size })
              : t("ticketList.bulkSelectHint")}
          </span>
          <div className="bulk-bar-group">
            <select
              value={bulkTeamId}
              onChange={(e) => setBulkTeamId(e.target.value)}
              disabled={selected.size === 0 || applying || applyingStatus}
              aria-label={t("ticketList.bulkTeamAria")}
            >
              <option value="">{t("tickets.noTeam")}</option>
              {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
            </select>
            <button
              type="button"
              className="primary"
              disabled={selected.size === 0 || applying || applyingStatus}
              onClick={applyBulk}
            >
              {applying ? t("ticketList.bulkApplying") : t("ticketList.bulkAssign")}
            </button>
          </div>
          <div className="bulk-bar-group">
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              disabled={selected.size === 0 || applying || applyingStatus}
              aria-label={t("ticketList.bulkStatusAria")}
            >
              <option value="">{t("ticketList.bulkStatusPlaceholder")}</option>
              <option value="new">{t("status.new")}</option>
              <option value="working">{t("status.working")}</option>
              <option value="waiting_feedback">{t("status.waiting_feedback")}</option>
              <option value="resolved">{t("status.resolved")}</option>
              <option value="closed">{t("status.closed")}</option>
              <option value="canceled">{t("status.canceled")}</option>
            </select>
            <button
              type="button"
              className="primary"
              disabled={selected.size === 0 || !bulkStatus || applying || applyingStatus}
              onClick={applyBulkStatus}
            >
              {applyingStatus ? t("ticketList.bulkApplying") : t("ticketList.bulkSetStatus")}
            </button>
          </div>
          {bulkInfo && <span style={{ color: "var(--text-muted)" }}>{bulkInfo}</span>}
          {bulkError && <span style={{ color: "var(--danger)" }}>{bulkError}</span>}
        </div>
      )}

      {loading && <p>{t("common.loading")}</p>}

      <div className="tickets-table-wrap">
        <table>
          <thead>
            <tr>
              {canBulkAssign && (
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    aria-label={t("ticketList.selectAll")}
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                  />
                </th>
              )}
              {header("ticket_number", t("ticket.number"))}
              {header("subject", t("ticket.subject"))}
              <th>{t("ticket.contact")}</th>
              {header("status", t("ticket.status"))}
              {header("priority", t("ticket.priority"))}
              <th>{t("ticket.team")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((tk) => (
              <tr key={tk.id}>
                {canBulkAssign && (
                  <td>
                    <input
                      type="checkbox"
                      aria-label={t("ticketList.selectRow")}
                      checked={selected.has(tk.id)}
                      onChange={() => toggleOne(tk.id)}
                    />
                  </td>
                )}
                <td><Link to={detailTo(tk.id)}>#{tk.ticket_number}</Link></td>
                <td>{tk.subject}{tk.is_private && <span className="badge role-badge" style={{ marginLeft: 6 }}>{t("tickets.privateBadge")}</span>}{isAwaitingReply(tk) && <span className="badge badge-awaiting" style={{ marginLeft: 6 }}>{t("ticketList.awaitingReply")}</span>}</td>
                <td><ContactCell name={[tk.contact_first_name, tk.contact_last_name].filter(Boolean).join(" ") || null} email={tk.contact_email} /></td>
                <td><StatusBadge status={tk.status} /></td>
                <td><PriorityBadge priority={tk.priority} /></td>
                <td>{tk.team_name ?? <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: same stacked-card list as ListView, but with a
          checkbox in the card header and the team name in place of
          the SLA / last-update line. */}
      <div className="tickets-cards">
        {rows.map((tk) => {
          const contactLabel = [tk.contact_first_name, tk.contact_last_name].filter(Boolean).join(" ") || tk.contact_email || "—";
          const isSelected = selected.has(tk.id);
          return (
            <article key={tk.id} className={`ticket-card ${isSelected ? "ticket-card-selected" : ""}`}>
              <header className="ticket-card-header">
                {canBulkAssign && (
                  <input
                    type="checkbox"
                    aria-label={t("ticketList.selectRow")}
                    checked={isSelected}
                    onChange={() => toggleOne(tk.id)}
                  />
                )}
                <Link to={detailTo(tk.id)} className="ticket-card-link" style={{ flex: "1 1 auto" }}>
                  <span className="ticket-card-num">#{tk.ticket_number}</span>
                </Link>
                <StatusBadge status={tk.status} />
              </header>
              <Link to={detailTo(tk.id)} className="ticket-card-link">
                <div className="ticket-card-subject">
                  {tk.subject}
                  {tk.is_private && <span className="badge role-badge" style={{ marginLeft: 6 }}>{t("tickets.privateBadge")}</span>}{isAwaitingReply(tk) && <span className="badge badge-awaiting" style={{ marginLeft: 6 }}>{t("ticketList.awaitingReply")}</span>}
                </div>
                <div className="ticket-card-contact">{contactLabel}</div>
                <div className="ticket-card-meta">
                  <PriorityBadge priority={tk.priority} />
                  <span>{t("ticket.team")}: {tk.team_name ?? "—"}</span>
                </div>
              </Link>
            </article>
          );
        })}
        {!loading && rows.length === 0 && (
          <p style={{ color: "var(--text-muted)", textAlign: "center", padding: 12 }}>
            {t("ticketList.kanbanEmpty")}
          </p>
        )}
      </div>

      <div className="pagination">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)}>{t("common.previous")}</button>
        <span style={{ alignSelf: "center" }}>{t("common.page")} {page} {t("common.of")} {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => onPage(page + 1)}>{t("common.next")}</button>
      </div>
    </div>
  );
}

interface KanbanViewProps {
  rows: TicketSummary[];
  loading: boolean;
  canMove: boolean;
  onMove: (ticketId: string, newStatus: TicketStatus) => void | Promise<void>;
  // Which kanban columns to render. The parent computes this from
  // the status chip filter so closed/canceled columns can be
  // toggled away without changing the kanban code.
  visibleStatuses: Set<TicketStatus>;
  detailTo: DetailToFn;
}

function KanbanView({ rows, loading, canMove, onMove, visibleStatuses, detailTo }: KanbanViewProps) {
  const t = useT();
  const navigate = useNavigate();
  const [dragOver, setDragOver] = useState<TicketStatus | null>(null);
  // Tracks which card's "Move" menu is open. Null = none. The menu is the
  // explicit fallback for users who don't realise the cards can be dragged.
  const [moveMenuFor, setMoveMenuFor] = useState<string | null>(null);

  // Pointer-event-based drag for touch devices. HTML5 drag-and-drop never
  // fires on touch, so we mirror it manually: pointerdown on a card starts
  // tracking, pointermove past a small threshold creates a floating ghost
  // element that follows the finger and highlights the column under it,
  // and pointerup looks up the column and calls onMove. Mouse pointers
  // are ignored here so the existing HTML5 DnD path keeps owning desktop.
  const touchDragRef = useRef<{
    ticketId: string;
    fromStatus: TicketStatus;
    pointerId: number;
    startX: number;
    startY: number;
    moved: boolean;
    ghost: HTMLElement | null;
    sourceCard: HTMLElement;
  } | null>(null);
  // After a successful drag we suppress the synthetic click so the card's
  // onClick doesn't navigate to the ticket detail.
  const suppressClickRef = useRef(false);

  const removeGhost = () => {
    const drag = touchDragRef.current;
    if (drag?.ghost && drag.ghost.parentNode) {
      drag.ghost.parentNode.removeChild(drag.ghost);
    }
    if (drag) drag.ghost = null;
  };

  const onCardPointerDown = (e: React.PointerEvent<HTMLDivElement>, ticket: TicketSummary) => {
    if (!canMove) return;
    if (e.pointerType === "mouse") return; // desktop uses HTML5 DnD
    // Don't hijack the pointer when the user is tapping the move menu.
    if ((e.target as HTMLElement).closest(".kanban-card-move")) return;
    const card = e.currentTarget;
    touchDragRef.current = {
      ticketId: ticket.id,
      fromStatus: ticket.status,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      ghost: null,
      sourceCard: card,
    };
  };

  const onCardPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = touchDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < 8) return; // tap, not a drag
    if (!drag.moved) {
      drag.moved = true;
      drag.sourceCard.setPointerCapture(e.pointerId);
      const ghost = drag.sourceCard.cloneNode(true) as HTMLElement;
      ghost.style.position = "fixed";
      ghost.style.pointerEvents = "none";
      ghost.style.opacity = "0.85";
      ghost.style.zIndex = "9999";
      ghost.style.width = `${drag.sourceCard.offsetWidth}px`;
      ghost.style.left = "0";
      ghost.style.top = "0";
      ghost.style.boxShadow = "0 12px 28px rgba(0, 0, 0, 0.18)";
      document.body.appendChild(ghost);
      drag.ghost = ghost;
      drag.sourceCard.style.opacity = "0.3";
    }
    if (drag.ghost) {
      drag.ghost.style.transform = `translate(${e.clientX - drag.ghost.offsetWidth / 2}px, ${e.clientY - 24}px)`;
    }
    // Highlight the column the finger is over via elementFromPoint.
    const under = document.elementFromPoint(e.clientX, e.clientY);
    const colEl = under?.closest("[data-kanban-column]") as HTMLElement | null;
    const status = (colEl?.dataset.kanbanColumn ?? null) as TicketStatus | null;
    setDragOver(status);
  };

  const finishTouchDrag = (e: React.PointerEvent<HTMLDivElement>, commit: boolean) => {
    const drag = touchDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (drag.moved) {
      removeGhost();
      drag.sourceCard.style.opacity = "";
      try { drag.sourceCard.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
      if (commit) {
        const under = document.elementFromPoint(e.clientX, e.clientY);
        const colEl = under?.closest("[data-kanban-column]") as HTMLElement | null;
        const status = (colEl?.dataset.kanbanColumn ?? null) as TicketStatus | null;
        if (status && status !== drag.fromStatus) {
          void onMove(drag.ticketId, status);
        }
      }
      // Block the click that fires after pointerup so we don't navigate
      // into the ticket the user just dropped.
      suppressClickRef.current = true;
      setTimeout(() => { suppressClickRef.current = false; }, 200);
    }
    setDragOver(null);
    touchDragRef.current = null;
  };

  const grouped = useMemo(() => {
    const out: Record<TicketStatus, TicketSummary[]> = {
      new: [], working: [], waiting_feedback: [], resolved: [], closed: [], canceled: [],
    };
    for (const tk of rows) (out[tk.status] ??= []).push(tk);
    return out;
  }, [rows]);

  // Close the open move menu when the user taps outside any card.
  useEffect(() => {
    if (!moveMenuFor) return;
    const close = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el?.closest(".kanban-card")) setMoveMenuFor(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [moveMenuFor]);

  return (
    <div className="kanban">
      {loading && <p style={{ gridColumn: "1 / -1" }}>{t("common.loading")}</p>}
      {KANBAN_COLUMNS.filter((status) => visibleStatuses.has(status)).map((status) => {
        const list = grouped[status] ?? [];
        const isOver = dragOver === status;
        return (
          <div
            key={status}
            className={`kanban-column ${isOver ? "drag-over" : ""}`}
            data-kanban-column={status}
            onDragOver={(e) => {
              if (!canMove) return;
              e.preventDefault();
              setDragOver(status);
            }}
            onDragLeave={() => setDragOver((s) => (s === status ? null : s))}
            onDrop={(e) => {
              if (!canMove) return;
              e.preventDefault();
              setDragOver(null);
              const id = e.dataTransfer.getData("text/plain");
              if (id) onMove(id, status);
            }}
          >
            <div className="kanban-header">
              <span>{t(`status.${status}`)}</span>
              <span className="kanban-count">{list.length}</span>
            </div>
            <div className="kanban-cards">
              {list.map((tk) => {
                const sla = slaRemaining(tk.sla_due_at, tk.status);
                const menuOpen = moveMenuFor === tk.id;
                return (
                  <div
                    key={tk.id}
                    className="kanban-card"
                    draggable={canMove}
                    style={canMove ? { touchAction: "none" } : undefined}
                    onDragStart={(e) => {
                      if (!canMove) return;
                      e.dataTransfer.setData("text/plain", tk.id);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onPointerDown={(e) => onCardPointerDown(e, tk)}
                    onPointerMove={onCardPointerMove}
                    onPointerUp={(e) => finishTouchDrag(e, true)}
                    onPointerCancel={(e) => finishTouchDrag(e, false)}
                    onClick={(e) => {
                      // Skip navigation right after a successful touch drag
                      // and when interacting with the move menu — the
                      // menu's buttons own their click handlers.
                      if (suppressClickRef.current) return;
                      if ((e.target as HTMLElement).closest(".kanban-card-move")) return;
                      navigate(detailTo(tk.id));
                    }}
                  >
                    <div className="kanban-card-num">
                      #{tk.ticket_number}
                      {tk.is_private && <span className="badge role-badge" style={{ marginLeft: 6 }}>{t("tickets.privateBadge")}</span>}{isAwaitingReply(tk) && <span className="badge badge-awaiting" style={{ marginLeft: 6 }}>{t("ticketList.awaitingReply")}</span>}
                      {canMove && (
                        <span className="kanban-card-move">
                          <button
                            type="button"
                            className="kanban-card-move-btn"
                            aria-label={t("kanban.move")}
                            aria-expanded={menuOpen}
                            onClick={(e) => {
                              e.stopPropagation();
                              setMoveMenuFor(menuOpen ? null : tk.id);
                            }}
                          >
                            ⋯
                          </button>
                          {menuOpen && (
                            <div className="kanban-card-move-menu" role="menu">
                              <div className="kanban-card-move-heading">{t("kanban.moveTo")}</div>
                              {KANBAN_COLUMNS.filter((s) => s !== tk.status).map((s) => (
                                <button
                                  type="button"
                                  role="menuitem"
                                  key={s}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMoveMenuFor(null);
                                    onMove(tk.id, s);
                                  }}
                                >
                                  {t(`status.${s}`)}
                                </button>
                              ))}
                            </div>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="kanban-card-subject">{tk.subject}</div>
                    <div className="kanban-card-meta">
                      <PriorityBadge priority={tk.priority} />
                      <span>{[tk.contact_first_name, tk.contact_last_name].filter(Boolean).join(" ") || tk.contact_email || "—"}</span>
                    </div>
                    {tk.sla_due_at && (
                      <div className={`kanban-card-sla sla-${sla.state}`}>{t("ticket.sla")}: {sla.label}</div>
                    )}
                  </div>
                );
              })}
              {list.length === 0 && !loading && (
                <div className="kanban-empty">{t("ticketList.kanbanEmpty")}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
